import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * HUDPageHeader — En-tête réutilisable pour les pages internes (V1 migrées en HUD).
 * Aligné sur le design system Cockpit (design-tokens.css). API préservée pour
 * compatibilité avec les pages existantes.
 *
 * Usage:
 *   <HUDPageHeader
 *     module="Cockpit"
 *     breadcrumb={[{ label: 'Hub', to: '/dashboard' }, { label: 'Projets' }]}
 *     title="Mes projets"
 *     subtitle="Gérez et publiez vos shoeboxes 3D."
 *     icon={FolderOpen}
 *     actions={<Button>Nouveau projet</Button>}
 *   />
 */
const HUDPageHeader = ({
  module = 'Cockpit',
  breadcrumb = [],
  title,
  subtitle,
  actions,
  icon: Icon,
}) => {
  const hasTitleRow = !!(title || subtitle || actions);
  return (
    <div
      className={`hud-page-header relative px-4 sm:px-6 lg:px-8 ${hasTitleRow ? 'pt-6 pb-5' : 'pt-3 pb-2'}`}
      data-testid="hud-page-header"
      style={{ borderBottom: '1px solid var(--c-border)', ...(hasTitleRow ? {} : { marginBottom: 0 }) }}
    >
      {/* Glow accent line top */}
      <div
        className="pointer-events-none absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(to right, transparent, var(--c-gold-glow), transparent)' }}
      />

      {/* Breadcrumbs eyebrow */}
      <nav
        className={`flex items-center gap-1.5 hud-eyebrow flex-wrap ${hasTitleRow ? 'mb-3' : 'mb-0'}`}
        aria-label="breadcrumbs"
      >
        <span className="hud-eyebrow hud-eyebrow-gold">▸ {module}</span>
        {breadcrumb.length > 0 && (
          <>
            <ChevronRight className="w-3 h-3 opacity-30" />
            {breadcrumb.map((b, i) => {
              const isLast = i === breadcrumb.length - 1;
              return (
                <span key={i} className="flex items-center gap-1.5">
                  {b.to ? (
                    <Link
                      to={b.to}
                      className="hud-eyebrow hover:opacity-80 transition-opacity"
                      style={{ color: 'var(--c-text-2)' }}
                    >
                      {b.label}
                    </Link>
                  ) : (
                    <span className={`hud-eyebrow ${isLast ? 'hud-eyebrow-gold' : ''}`}>
                      {b.label}
                    </span>
                  )}
                  {!isLast && <ChevronRight className="w-3 h-3 opacity-30" />}
                </span>
              );
            })}
          </>
        )}
      </nav>

      {/* Title row — masqué si ni titre, ni sous-titre, ni actions */}
      {hasTitleRow && (
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1 basis-[380px] shrink-0">
          {Icon && (
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{
                background: 'var(--c-gold-soft)',
                border: '1px solid var(--c-border-accent)',
                color: 'var(--c-gold)',
                boxShadow: 'var(--shadow-glow)',
              }}
            >
              <Icon className="w-5 h-5" strokeWidth={1.75} />
            </div>
          )}
          <div className="min-w-0">
            <h1
              className="font-semibold tracking-tight leading-tight truncate"
              data-testid="hud-page-title"
              style={{
                fontFamily: 'Outfit, sans-serif',
                color: 'var(--c-text)',
                fontSize: 'clamp(24px, 2.6vw, 32px)',
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                className="text-[14px] leading-relaxed mt-1.5"
                style={{ color: 'var(--c-text-2)' }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-wrap justify-end min-w-0 flex-1" data-testid="hud-page-actions">
            {actions}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default HUDPageHeader;
